import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Checkbox,
    FormGroup,
    IconButton,
    Tooltip,
    makeStyles,
    CircularProgress,
} from '@material-ui/core';
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons';
import { ResponsiveDataView, Column } from '../../shared/ResponsiveDataView';
import { FormSection } from '../../shared/formSection';
import { FormField } from '../../shared/formField';
import { AlarmNotificationSubscription } from '../../../dtos/alarmNotificationSubscription';
import { useApi } from '../../useApi';
import { AjaxResult } from '../../../enums/ajaxResult';
import { useSnackbar } from 'notistack';

interface Props {
    open: boolean;
    onClose: () => void;
    siteName: string;
    orgId?: number | null;
    siteId?: number | null;
}

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        minWidth: '700px',
        maxWidth: '900px',
    },
    subscriptionsSection: {
        marginBottom: '30px',
    },
    emptyState: {
        padding: '40px',
        textAlign: 'center',
        color: theme.palette.grey[500],
        fontStyle: 'italic',
    },
    formSection: {
        marginTop: '20px',
        padding: '20px',
        backgroundColor: theme.palette.grey[50],
        borderRadius: '4px',
    },
    alignedFormField: {
        '& > div': {
            display: 'flex',
            alignItems: 'center',
            '& > label': {
                minWidth: '170px',
                marginRight: '10px',
            },
        },
    },
    alignedFormFieldTop: {
        '& > div': {
            display: 'flex',
            alignItems: 'flex-start',
            '& > label': {
                minWidth: '170px',
                marginRight: '10px',
                paddingTop: '9px',
            },
        },
    },
    alarmCheckboxGroup: {
        marginLeft: '20px',
        marginTop: '10px',
    },
    alarmCheckboxSection: {
        marginTop: '15px',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
    },
    addButton: {
        marginTop: '10px',
    },
    loadingContainer: {
        textAlign: 'center',
        padding: '40px',
    },
    maxWidthCell: {
        maxWidth: '300px',
    },
    buttonGroup: {
        marginTop: '20px',
        display: 'flex',
        gap: '10px',
    },
}));

// System Error Alarms (EventType = 2)
const SYSTEM_ERROR_ALARMS = [
    { id: 0, name: 'Sensor Battery Low' },
    { id: 1, name: 'Inconsistent Box' },
    { id: 2, name: 'Sensor Tamper' },
    { id: 3, name: 'Sensor Missing' },
    { id: 4, name: 'Biometric Device Unavailable' },
    { id: 5, name: 'DMP Panel Unavailable' },
    { id: 8, name: 'HMI Unavailable' },
];

// Room Status Alarms (EventType = 3)
const ROOM_STATUS_ALARMS = [
    { id: 0, name: 'Unauthorized Box Opening' },
    { id: 10, name: 'Motion Stopped During Session' },
    { id: 20, name: 'Door Left Open' },
    { id: 30, name: 'Unauthorized Door Opening' },
    { id: 40, name: 'No Box Opened' },
    { id: 41, name: 'Box Left Open' },
    { id: 50, name: 'Unauthorized Motion Detected' },
];

export function AlarmNotificationSubscriptionsModal({ open, onClose, siteName, orgId, siteId }: Props) {
    const classes = useStyles();
    const { alarmNotificationSubscriptionApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [subscriptions, setSubscriptions] = useState<AlarmNotificationSubscription[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Any authenticated user can create/update/delete subscriptions
    const canModifySubscriptions = true;
    const [editingSubscription, setEditingSubscription] = useState<AlarmNotificationSubscription | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    // Form state
    const [emailAddress, setEmailAddress] = useState('');
    const [alarmTypeFilter, setAlarmTypeFilter] = useState<'all' | 'systemErrors' | 'roomStatus' | 'custom'>('all');
    const [selectedSystemErrors, setSelectedSystemErrors] = useState<number[]>([]);
    const [selectedRoomStatusAlarms, setSelectedRoomStatusAlarms] = useState<number[]>([]);
    const [emailError, setEmailError] = useState('');

    // Ref for scrolling to form section
    const formSectionRef = useRef<HTMLDivElement>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout>();

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^(?!.*\.\.)[a-zA-Z0-9](?:[a-zA-Z0-9.'_%+-]*[a-zA-Z0-9])?@(?:(?!-)[A-Za-z0-9-]+(?<!-)\.)+[A-Za-z]{2,}$/;
        return emailRegex.test(email);
    };

    // Helper: Scroll to bottom of dialog
    const scrollToBottom = useCallback(() => {
        scrollTimeoutRef.current = setTimeout(() => {
            if (dialogContentRef.current) {
                dialogContentRef.current.scrollTop = dialogContentRef.current.scrollHeight;
            }
        }, 100);
    }, []);

    // Helper: Scroll to form section
    const scrollToForm = useCallback(() => {
        scrollTimeoutRef.current = setTimeout(() => {
            if (formSectionRef.current) {
                formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }, []);

    // Helper: Handle API result and show notifications
    const handleApiResult = useCallback((result: any, successMessage: string): boolean => {
        if (result.result === AjaxResult.Success && result.data) {
            enqueueSnackbar(successMessage, { variant: 'success' });
            return true;
        } else if (result.result === AjaxResult.Duplicate && result.messages) {
            enqueueSnackbar(result.messages.join(', '), { variant: 'warning' });
            return false;
        } else if (result.result === AjaxResult.Failure && result.messages) {
            enqueueSnackbar(result.messages.join(', '), { variant: 'error' });
            return false;
        } else {
            enqueueSnackbar('Operation failed', { variant: 'error' });
            return false;
        }
    }, [enqueueSnackbar]);

    // Helper: Determine alarm type from subscription
    const getAlarmTypeFromSubscription = useCallback((sub: AlarmNotificationSubscription): 'all' | 'systemErrors' | 'roomStatus' | 'custom' => {
        if (sub.subscribeToAllAlarms) return 'all';
        if (sub.subscribeToSystemErrors && !sub.subscribeToRoomStatusAlarms) return 'systemErrors';
        if (sub.subscribeToRoomStatusAlarms && !sub.subscribeToSystemErrors) return 'roomStatus';
        return 'custom';
    }, []);

    // Helper: Validate form
    const validateForm = useCallback((): { isValid: boolean; error?: string } => {
        if (!emailAddress || !validateEmail(emailAddress)) {
            return { isValid: false, error: 'Please enter a valid email address' };
        }

        if (alarmTypeFilter === 'systemErrors' && selectedSystemErrors.length === 0) {
            return { isValid: false, error: 'Please select at least one system error alarm' };
        }

        if (alarmTypeFilter === 'roomStatus' && selectedRoomStatusAlarms.length === 0) {
            return { isValid: false, error: 'Please select at least one room status alarm' };
        }

        if (alarmTypeFilter === 'custom' && selectedSystemErrors.length === 0 && selectedRoomStatusAlarms.length === 0) {
            return { isValid: false, error: 'Please select at least one alarm' };
        }

        return { isValid: true };
    }, [emailAddress, alarmTypeFilter, selectedSystemErrors, selectedRoomStatusAlarms]);

    // Helper: Build request data
    const buildRequestData = useCallback(() => ({
        emailAddress,
        employeeId: undefined,
        subscribeToAllAlarms: alarmTypeFilter === 'all',
        subscribeToSystemErrors: alarmTypeFilter === 'systemErrors' || (alarmTypeFilter === 'custom' && selectedSystemErrors.length > 0),
        subscribeToRoomStatusAlarms: alarmTypeFilter === 'roomStatus' || (alarmTypeFilter === 'custom' && selectedRoomStatusAlarms.length > 0),
        systemErrorNames: (alarmTypeFilter === 'systemErrors' || alarmTypeFilter === 'custom') ? selectedSystemErrors : undefined,
        roomStatusAlarmNames: (alarmTypeFilter === 'roomStatus' || alarmTypeFilter === 'custom') ? selectedRoomStatusAlarms : undefined,
        isActive: true,
    }), [emailAddress, alarmTypeFilter, selectedSystemErrors, selectedRoomStatusAlarms]);

    const loadSubscriptions = useCallback(async () => {
        if (!orgId || !siteId) return;

        setIsLoading(true);
        try {
            const result = await alarmNotificationSubscriptionApi.getAllForSite(orgId, siteId);
            if (result.result === AjaxResult.Success && result.data) {
                setSubscriptions(result.data);
            } else {
                enqueueSnackbar('Failed to load alarm notification subscriptions', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error loading alarm notification subscriptions', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [orgId, siteId, alarmNotificationSubscriptionApi, enqueueSnackbar]);

    // Load subscriptions when modal opens
    useEffect(() => {
        if (open && orgId && siteId) {
            loadSubscriptions();
        }
    }, [open, orgId, siteId, loadSubscriptions]);

    const handleAdd = () => {
        // Use a partial object without unsafe type assertion
        setEditingSubscription({ id: 0 } as AlarmNotificationSubscription);
        scrollToForm();
    };

    const handleEdit = (subscription: AlarmNotificationSubscription) => {
        setEditingSubscription(subscription);
        setEmailAddress(subscription.emailAddress);

        const alarmType = getAlarmTypeFromSubscription(subscription);
        setAlarmTypeFilter(alarmType);

        if (alarmType === 'systemErrors' || alarmType === 'custom') {
            setSelectedSystemErrors(subscription.systemErrorNames || []);
        }
        if (alarmType === 'roomStatus' || alarmType === 'custom') {
            setSelectedRoomStatusAlarms(subscription.roomStatusAlarmNames || []);
        }

        scrollToForm();
    };

    const handleDelete = (id: number) => {
        setDeleteConfirmId(id);
    };

    const confirmDelete = async () => {
        if (deleteConfirmId !== null && orgId && siteId) {
            try {
                const result = await alarmNotificationSubscriptionApi.deleteForSite(orgId, siteId, deleteConfirmId);
                if (result.result === AjaxResult.Success) {
                    enqueueSnackbar('Alarm subscription deleted successfully', { variant: 'success' });
                    setSubscriptions(subscriptions.filter(s => s.id !== deleteConfirmId));
                    setDeleteConfirmId(null);
                } else {
                    enqueueSnackbar('Failed to delete alarm subscription', { variant: 'error' });
                }
            } catch (error) {
                enqueueSnackbar('Error deleting alarm subscription', { variant: 'error' });
            }
        }
    };

    const handleSave = async () => {
        if (!orgId || !siteId) return;

        // Validate form
        const validation = validateForm();
        if (!validation.isValid) {
            if (validation.error?.includes('email')) {
                setEmailError(validation.error);
            } else {
                enqueueSnackbar(validation.error!, { variant: 'warning' });
            }
            return;
        }

        setIsLoading(true);
        try {
            const requestData = buildRequestData();
            const isUpdate = !!(editingSubscription?.id);

            const result = isUpdate
                ? await alarmNotificationSubscriptionApi.updateForSite(orgId, siteId, editingSubscription.id!, requestData)
                : await alarmNotificationSubscriptionApi.createForSite(orgId, siteId, requestData);

            const success = handleApiResult(
                result,
                isUpdate ? 'Alarm subscription updated successfully' : 'Alarm subscription created successfully'
            );

            if (success && result.data) {
                if (isUpdate) {
                    setSubscriptions(subscriptions.map(s => s.id === editingSubscription.id ? result.data! : s));
                } else {
                    setSubscriptions([...subscriptions, result.data]);
                }
                handleCancel();
                scrollToBottom();
            }
        } catch (error) {
            enqueueSnackbar('Error saving alarm subscription', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEditingSubscription(null);
        setEmailAddress('');
        setAlarmTypeFilter('all');
        setSelectedSystemErrors([]);
        setSelectedRoomStatusAlarms([]);
        setEmailError('');
    };

    const handleSystemErrorToggle = (id: number) => {
        setSelectedSystemErrors(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleRoomStatusAlarmToggle = (id: number) => {
        setSelectedRoomStatusAlarms(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const getAlarmTypeDisplay = (subscription: AlarmNotificationSubscription): string => {
        if (subscription.subscribeToAllAlarms) return 'All Alarms';
        if (subscription.subscribeToSystemErrors && !subscription.subscribeToRoomStatusAlarms) return 'System Errors';
        if (subscription.subscribeToRoomStatusAlarms && !subscription.subscribeToSystemErrors) return 'Room Status Alarms';
        return 'Custom';
    };

    const getSpecificAlarmsDisplay = (subscription: AlarmNotificationSubscription): string => {
        if (subscription.subscribeToAllAlarms) return 'All';

        let systemErrorsDisplay = '';
        if (subscription.systemErrorNames) {
            if (subscription.systemErrorNames.length === SYSTEM_ERROR_ALARMS.length) {
                systemErrorsDisplay = 'All System Errors';
            } else {
                const names = subscription.systemErrorNames
                    .map(id => SYSTEM_ERROR_ALARMS.find(a => a.id === id))
                    .filter(alarm => alarm !== undefined)
                    .map(alarm => alarm!.name);
                systemErrorsDisplay = names.join(', ');
            }
        }

        let roomStatusDisplay = '';
        if (subscription.roomStatusAlarmNames) {
            if (subscription.roomStatusAlarmNames.length === ROOM_STATUS_ALARMS.length) {
                roomStatusDisplay = 'All Room Status Alarms';
            } else {
                const names = subscription.roomStatusAlarmNames
                    .map(id => ROOM_STATUS_ALARMS.find(a => a.id === id))
                    .filter(alarm => alarm !== undefined)
                    .map(alarm => alarm!.name);
                roomStatusDisplay = names.join(', ');
            }
        }

        const displays: string[] = [];
        if (systemErrorsDisplay) displays.push(systemErrorsDisplay);
        if (roomStatusDisplay) displays.push(roomStatusDisplay);

        return displays.join(', ') || 'None';
    };

    // Memoized computed values
    const showSystemErrorCheckboxes = useMemo(
        () => alarmTypeFilter === 'systemErrors' || alarmTypeFilter === 'custom',
        [alarmTypeFilter]
    );
    const showRoomStatusCheckboxes = useMemo(
        () => alarmTypeFilter === 'roomStatus' || alarmTypeFilter === 'custom',
        [alarmTypeFilter]
    );

    return (
        <>
            <Dialog open={open} maxWidth="md" fullWidth>
                <DialogTitle>Alarm Notifications for {siteName}</DialogTitle>

                <DialogContent className={classes.dialogContent} ref={dialogContentRef}>
                    {/* Existing Subscriptions List */}
                    <div className={classes.subscriptionsSection}>
                        <h3>Existing Subscriptions</h3>
                        {isLoading ? (
                            <div className={classes.loadingContainer}>
                                <CircularProgress />
                            </div>
                        ) : subscriptions.length === 0 ? (
                            <div className={classes.emptyState}>
                                No alarm subscriptions configured. Click 'Add Subscription' to create email notification subscriptions for specific alarm types.
                            </div>
                        ) : (
                            <ResponsiveDataView
                                rows={subscriptions}
                                rowKey={(s) => s.id}
                                columns={[
                                    { key: 'emailAddress', label: 'Email Address', primary: true },
                                    { key: 'alarmType', label: 'Alarm Type', render: (s) => getAlarmTypeDisplay(s) },
                                    {
                                        key: 'specificAlarms',
                                        label: 'Specific Alarms',
                                        render: (s) => (
                                            <span className={classes.maxWidthCell}>
                                                {getSpecificAlarmsDisplay(s)}
                                            </span>
                                        ),
                                    },
                                ] as Column<AlarmNotificationSubscription>[]}
                                actions={canModifySubscriptions
                                    ? (subscription) => (
                                        <>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => handleEdit(subscription)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => handleDelete(subscription.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )
                                    : undefined}
                            />
                        )}
                        {!editingSubscription && canModifySubscriptions && (
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.addButton}
                                onClick={handleAdd}
                            >
                                Add Subscription
                            </Button>
                        )}
                    </div>

                    {/* Add/Edit Subscription Form */}
                    {editingSubscription !== null && (
                        <div ref={formSectionRef}>
                            <FormSection headerText={editingSubscription.id ? 'Edit Subscription' : 'Add New Subscription'}>
                            <div className={classes.alignedFormField}>
                                <FormField label="Email Address" required>
                                    <FormControl fullWidth>
                                        <TextField
                                            value={emailAddress}
                                            onChange={(e) => {
                                                setEmailAddress(e.target.value);
                                                setEmailError('');
                                            }}
                                            placeholder="user@example.com"
                                            variant="outlined"
                                            error={!!emailError}
                                            helperText={emailError}
                                            InputProps={{ inputProps: { style: { padding: '8px' } } }}
                                        />
                                    </FormControl>
                                </FormField>
                            </div>

                            <div className={classes.alignedFormFieldTop}>
                                <FormField label="Alarm Type Filter" required>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={alarmTypeFilter}
                                            onChange={(e, value) => setAlarmTypeFilter(value as any)}
                                        >
                                            <FormControlLabel value="all" control={<Radio color="primary" />} label="All Alarms" />
                                            <FormControlLabel value="systemErrors" control={<Radio color="primary" />} label="System Errors only" />
                                            <FormControlLabel value="roomStatus" control={<Radio color="primary" />} label="Room Status Alarms only" />
                                            <FormControlLabel value="custom" control={<Radio color="primary" />} label="Custom (select specific alarms below)" />
                                        </RadioGroup>
                                    </FormControl>
                                </FormField>
                            </div>

                            {/* System Error Checkboxes */}
                            {showSystemErrorCheckboxes && (
                                <div className={classes.alarmCheckboxSection}>
                                    <strong>System Error Alarms:</strong>
                                    <FormGroup className={classes.alarmCheckboxGroup}>
                                        {SYSTEM_ERROR_ALARMS.map(alarm => (
                                            <FormControlLabel
                                                key={alarm.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedSystemErrors.includes(alarm.id)}
                                                        onChange={() => handleSystemErrorToggle(alarm.id)}
                                                        color="primary"
                                                    />
                                                }
                                                label={alarm.name}
                                            />
                                        ))}
                                    </FormGroup>
                                </div>
                            )}

                            {/* Room Status Alarm Checkboxes */}
                            {showRoomStatusCheckboxes && (
                                <div className={classes.alarmCheckboxSection}>
                                    <strong>Room Status Alarms:</strong>
                                    <FormGroup className={classes.alarmCheckboxGroup}>
                                        {ROOM_STATUS_ALARMS.map(alarm => (
                                            <FormControlLabel
                                                key={alarm.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedRoomStatusAlarms.includes(alarm.id)}
                                                        onChange={() => handleRoomStatusAlarmToggle(alarm.id)}
                                                        color="primary"
                                                    />
                                                }
                                                label={alarm.name}
                                            />
                                        ))}
                                    </FormGroup>
                                </div>
                            )}

                            <div className={classes.buttonGroup}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Save Subscription'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </FormSection>
                        </div>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    {(() => {
                        if (deleteConfirmId === null) return null;
                        const subscription = subscriptions.find(s => s.id === deleteConfirmId);
                        const emailAddress = subscription ? subscription.emailAddress : 'this email';
                        return (
                            <>
                                Are you sure you want to delete this alarm subscription for {emailAddress}?
                            </>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={confirmDelete} color="primary" variant="contained">
                        Confirm
                    </Button>
                    <Button onClick={() => setDeleteConfirmId(null)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
